import { useLocation, useNavigate } from "react-router-dom";
import { BreadCrumbData } from "@shared/lib/navigation/Breadcrumbs.types";
import { Account } from "@models/account";
import { editLastBread, getLastAvailableLink } from "../../../utils/breadcrumbs";
import {
  CUSTOMERS_BREADCRUMB,
  CUSTOMERS_PATH,
  CUSTOMERS_STRUCTURE_CRUMBS,
} from "./customers.constants";



/**
 * Builds breadcrumb trail for the customers section, preserving document-flow crumbs when present.
 * @param state router location state passed to editLastBread
 * @returns breadcrumb items for the customers list and parent ledger
 */
function buildCustomersBreadcrumb(state: unknown): BreadCrumbData[] {
  const base: BreadCrumbData[] = [
    { name: CUSTOMERS_BREADCRUMB.ledger },
    { name: CUSTOMERS_BREADCRUMB.list, link: CUSTOMERS_PATH },
  ];

  const stateRecord = state as { breadcrumbData?: BreadCrumbData[] } | null | undefined;
  return [...(editLastBread(stateRecord?.breadcrumbData, CUSTOMERS_PATH, state) ?? base)];
}



/**
 * Navigation helpers for the customers page: row pick in document flows and structure editor routes.
 * @returns rowChosen handler, structure edit navigators, and whether table rows are selectable
 */
export function useCustomersNavigation() {
  const location = useLocation();
  const navigate = useNavigate();



  /**
   * Selects a customer row and returns to the previous breadcrumb with document_account set.
   * @param row selected account row
   */
  function rowChosen<T extends Account>(row: T) {
    if (!location.state?.breadcrumbData) {
      return;
    }

    navigate(getLastAvailableLink(location.state.breadcrumbData), {
      replace: true,
      state: {
        ...location.state,
        document_account: { ...row, id: undefined, account_id: row.id },
        car: null,
        breadcrumbData: location.state.breadcrumbData.slice(0, -1),
      },
    });
  }



  /**
   * Navigates to the search or browse structure configuration screen.
   * @param kind "search" for filter fields, "browse" for table columns
   */
  function navigateToStructure(kind: "search" | "browse") {
    const currentState = { ...location.state };
    const breadcrumbData = buildCustomersBreadcrumb(currentState);
    const crumb =
      kind === "search"
        ? CUSTOMERS_STRUCTURE_CRUMBS.search
        : CUSTOMERS_STRUCTURE_CRUMBS.browse;

    navigate(`${CUSTOMERS_PATH}/structure/${kind}`, {
      replace: true,
      state: {
        ...currentState,
        breadcrumbData: [...breadcrumbData, { name: crumb }],
      },
    });
  }

  return {
    rowChosen,
    editSearchStructure: () => navigateToStructure("search"),
    editBrowseStructure: () => navigateToStructure("browse"),
    selectRow: Boolean(location.state?.breadcrumbData),
  };
}
